import { useState } from 'react';
import { ExpenseRow } from './ExpenseRow';
import { ReceiptViewerDialog } from './ReceiptViewerDialog';
import { SortableTableHeader } from '@/components/shared/SortableTableHeader';
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  useClientTableSort,
  byKey,
  type Comparator,
} from '@/hooks/useClientTableSort';
import { expenseProjectName } from '@jobos/shared';
import type { ExpenseWithDetails, ExpenseSortField } from '@jobos/shared';

// Defined at module scope so the memoized sort isn't invalidated each render.
const EXPENSE_COMPARATORS: Record<ExpenseSortField, Comparator<ExpenseWithDetails>> = {
  paidDate: byKey((e) => e.paidDate),
  expenseDate: byKey((e) => e.expenseDate),
  store: byKey((e) => e.storeName),
  category: byKey((e) => e.category.name),
  description: byKey((e) => e.description),
  project: byKey((e) => expenseProjectName(e)),
  amount: byKey((e) => e.amount, { numeric: true }),
  paymentMethod: byKey((e) => e.paymentMethod),
  reimburse: byKey((e) => (e.reimburse ? 1 : 0), { numeric: true }),
};

interface ExpensesTableProps {
  expenses: ExpenseWithDetails[];
  hasProjects: boolean;
  formattedTotal: string;
  onEdit: (expense: ExpenseWithDetails) => void;
  onDelete: (id: string) => void;
  initialSort: { field: ExpenseSortField; direction: 'asc' | 'desc' };
  onSortChange: (next: { field: ExpenseSortField; direction: 'asc' | 'desc' }) => void;
}

export function ExpensesTable({
  expenses,
  hasProjects,
  formattedTotal,
  onEdit,
  onDelete,
  initialSort,
  onSortChange,
}: ExpensesTableProps) {
  const { sorted, sortField, sortDirection, handleSort } = useClientTableSort<
    ExpenseWithDetails,
    ExpenseSortField
  >(expenses, EXPENSE_COMPARATORS, initialSort, { onSortChange });

  const onSort = (field: string) => handleSort(field as ExpenseSortField);

  const [viewingReceipt, setViewingReceipt] = useState<ExpenseWithDetails | null>(null);

  return (
    <>
    <Table>
      {/* No header to sort by on a phone — the stacked rows carry their own
          labels, and nine sort controls would cost a row of width each. The
          saved sort still applies; it is set on the desktop the office uses. */}
      <TableHeader className="hidden sm:table-header-group">
        <TableRow>
          <SortableTableHeader field="paidDate" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Paid Date
          </SortableTableHeader>
          <SortableTableHeader field="expenseDate" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Upload Date
          </SortableTableHeader>
          <SortableTableHeader field="store" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Store
          </SortableTableHeader>
          <SortableTableHeader field="category" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Category
          </SortableTableHeader>
          <SortableTableHeader field="description" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Description
          </SortableTableHeader>
          {hasProjects && (
            <SortableTableHeader field="project" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
              Project
            </SortableTableHeader>
          )}
          <SortableTableHeader field="paymentMethod" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Payment Method
          </SortableTableHeader>
          <SortableTableHeader field="reimburse" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort}>
            Reimburse
          </SortableTableHeader>
          <SortableTableHeader field="amount" currentSortField={sortField} currentSortDirection={sortDirection} onSort={onSort} className="text-right" align="right">
            Amount
          </SortableTableHeader>
          <TableHead className="text-right">Receipt</TableHead>
          <TableHead className="w-12" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {sorted.map((expense) => (
          <ExpenseRow
            key={expense.id}
            expense={expense}
            hasProjects={hasProjects}
            onEdit={onEdit}
            onDelete={onDelete}
            onViewReceipt={setViewingReceipt}
          />
        ))}
      </TableBody>
      <TableFooter>
        <TableRow className="flex items-center justify-between sm:table-row">
          <TableCell colSpan={hasProjects ? 8 : 7} className="font-semibold sm:text-right">
            Total
          </TableCell>
          <TableCell
            className={`font-semibold sm:text-right ${formattedTotal.startsWith('-') ? 'text-green-600' : ''}`}
          >
            {formattedTotal}
          </TableCell>
          {/* Spacers for the receipt and actions columns — nothing to line up
              against once the row is a flex line. */}
          <TableCell colSpan={2} className="hidden sm:table-cell" />
        </TableRow>
      </TableFooter>
    </Table>
    <ReceiptViewerDialog
      expense={viewingReceipt}
      onOpenChange={(open) => {
        if (!open) setViewingReceipt(null);
      }}
    />
    </>
  );
}
